import React from 'react';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { getSiteUrl } from '@utils/config';
import AreaHeader from '@components/Shared/AreaHeader/AreaHeader';
import { BuilderResourcesStyles } from './BuilderResources.styled';

const BuilderResources = () => {
  const router = useRouter();

  return (
    <BuilderResourcesStyles>
      <Head htmlAttributes={{ lang: 'en' }}>
        <title>Builder Resources | Clayton Homes</title>
        <meta
          name="description"
          content="To provide our customers with the best, we have provided brand specific installation instructions and resources for our builders and customers."
        />
        <link rel="canonical" href={`${getSiteUrl(router.pathname)}`} />
        <meta
          property="og:image"
          content={`${getSiteUrl('/public-assets/images/clayton-logo.jpg')}`}
        />
      </Head>

      <AreaHeader
        headerText="Builder Resources"
        bgImage="/public-assets/images/builder-resources/header-pattern.svg"
      />

      <div className="constrained-width">
        <h2>Site Construction Manual</h2>
        <p className="intro-p">
          Clayton is dedicated to ensuring each and every home is properly
          installed. In our efforts to provide our customers with the best home
          possible, we have provided brand specific installation instructions
          for our builders and customers. These documents provide our builders
          with important reference information that will allow them to better
          install your home to ensure your safety and protect your investment in
          your home.
        </p>
        <p>
          Builders please note that each brand specific manual contains slightly
          different structural connection requirements unique to the brand of
          home.
        </p>

        <div className="pdf-desktop-columns pdf-blurbs">
          <div className="pdf-desktop-column">
            <a
              href="https://p.widencdn.net/mkbctj/Legacy-Clayton"
              className="pdf-link gtm-maynardville-athens-pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="pdf-blurb">
                <div className="pdf-blurb-icon">
                  <Image
                    src="/public-assets/images/builder-resources/pdf-icon.png"
                    alt="PDF"
                    width={36}
                    height={44}
                  />
                </div>
                <div className="pdf-blurb-text">
                  <div className="h6">Home Building Facilities:</div>
                  <p>
                    Maynardville, Rutledge, Bean Station, Oxford, Waycross, Waco
                    1, Waco 2, Appalachia, Bonham, Savannah, Sulphur Springs,
                    Giles, and Athens
                  </p>
                </div>
              </div>
            </a>

            <a
              href="https://p.widencdn.net/lskwls/GW"
              className="pdf-link gtm-albany-pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="pdf-blurb">
                <div className="pdf-blurb-icon">
                  <Image
                    src="/public-assets/images/builder-resources/pdf-icon.png"
                    alt="PDF"
                    width={36}
                    height={44}
                  />
                </div>
                <div className="pdf-blurb-text">
                  <div className="h6">Home Building Facilities:</div>
                  <p>Albany*</p>
                </div>
              </div>
            </a>

            <a
              href="https://p.widencdn.net/z2fvuo/Cavalier"
              className="pdf-link gtm-nashville-pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="pdf-blurb">
                <div className="pdf-blurb-icon">
                  <Image
                    src="/public-assets/images/builder-resources/pdf-icon.png"
                    alt="PDF"
                    width={36}
                    height={44}
                  />
                </div>
                <div className="pdf-blurb-text">
                  <div className="h6">Home Building Facilities:</div>
                  <p>Nashville</p>
                </div>
              </div>
            </a>
          </div>
          {/* end pdf desktop column 1 */}

          <div className="pdf-desktop-column">
            <a
              href="https://p.widencdn.net/chggyp/Schult-Marlette"
              className="pdf-link gtm-buckeye-wakarusa-pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="pdf-blurb">
                <div className="pdf-blurb-icon">
                  <Image
                    src="/public-assets/images/builder-resources/pdf-icon.png"
                    alt="PDF"
                    width={36}
                    height={44}
                  />
                </div>
                <div className="pdf-blurb-text">
                  <div className="h6">Home Building Facilities:</div>
                  <p>
                    Buckeye, Hermiston, Lewistown, Rockwell, Richfield,
                    Middlebury, and Wakarusa*
                  </p>
                </div>
              </div>
            </a>

            <a
              href="https://p.widencdn.net/cjfkts/Karsten"
              className="pdf-link gtm-albuquerque-sacramento-pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="pdf-blurb">
                <div className="pdf-blurb-icon">
                  <Image
                    src="/public-assets/images/builder-resources/pdf-icon.png"
                    alt="PDF"
                    width={36}
                    height={44}
                  />
                </div>
                <div className="pdf-blurb-text">
                  <div className="h6">Home Building Facilities:</div>
                  <p>Albuquerque and Sacramento</p>
                </div>
              </div>
            </a>

            <a
              href="https://p.widencdn.net/trlek5/SE"
              className="pdf-link gtm-ftworth-addison-pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="pdf-blurb">
                <div className="pdf-blurb-icon">
                  <Image
                    src="/public-assets/images/builder-resources/pdf-icon.png"
                    alt="PDF"
                    width={36}
                    height={44}
                  />
                </div>
                <div className="pdf-blurb-text">
                  <div className="h6">Home Building Facilities:</div>
                  <p>Ft. Worth, Russellville, and Addison</p>
                </div>
              </div>
            </a>
          </div>
          {/* end pdf desktop column 2 */}
        </div>
        {/* end pdf desktop columns */}

        <p className="xsmall">
          * This manual covers both single and multi-section homes installed
          over pier and anchor and load bearing crawl space walls.
        </p>
      </div>

      <div className="full-width-bg">
        <div className="constrained-width tech-notes">
          <h2>Approved Technical Notes</h2>
          <p className="intro-p">
            We are dedicated to ensuring that builders install our homes in a
            manner that ensures the homeowner receives the safest and most
            durable home possible. This section contains supplemental
            information that is applicable to all brands. This section provides
            additional information that addresses issues not contained in the
            manual. Much of this information has been provided by other builders
            or inspectors to help raise the bar on the overall quality of the
            installation of the home. We have reviewed this information and
            believe it to be technically correct.
          </p>
          <p className="intro-p">
            Builders should review local codes and requirements before
            implementing information contained in this section.
          </p>

          <div className="pdf-desktop-columns pdf-blurbs">
            {/* If we add more PDFs, add columns like Site Construction Manual section above. */}
            <a
              href="https://claytonhomes.widen.net/s/zwrhrghjzr/hud-code-3285"
              className="pdf-link gtm-hud-code-3285-pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="pdf-blurb">
                <div className="pdf-blurb-icon">
                  <Image
                    src="/public-assets/images/builder-resources/pdf-icon.png"
                    alt="PDF"
                    width={36}
                    height={44}
                  />
                </div>
                <div className="pdf-blurb-text">
                  <div className="h6">HUD Code 3285 Installation Code</div>
                </div>
              </div>
            </a>
          </div>
          <div className="pdf-desktop-columns pdf-blurbs">
            <a
              href="https://claytonhomes.widen.net/s/7zztnmpmnm/clayton-homes-crane-set-guidelines"
              className="pdf-link gtm-crane-set-guidelines-pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="pdf-blurb">
                <div className="pdf-blurb-icon">
                  <Image
                    src="/public-assets/images/builder-resources/pdf-icon.png"
                    alt="PDF"
                    width={36}
                    height={44}
                  />
                </div>
                <div className="pdf-blurb-text">
                  <div className="h6">Crane Set Guidelines</div>
                </div>
              </div>
            </a>
          </div>
          <div className="pdf-desktop-columns pdf-blurbs">
            <a
              href="https://claytonhomes.widen.net/s/cjdzz9fwql/clayton-homes-home-handling-guidelines"
              className="pdf-link gtm-home-handling-guidelines"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="pdf-blurb">
                <div className="pdf-blurb-icon">
                  <Image
                    src="/public-assets/images/builder-resources/pdf-icon.png"
                    alt="PDF"
                    width={36}
                    height={44}
                  />
                </div>
                <div className="pdf-blurb-text">
                  <div className="h6">Home Handling Guidelines </div>
                </div>
              </div>
            </a>
          </div>
          {/* end pdf desktop columns */}
        </div>
      </div>

      <div className="constrained-width foundation-sys">
        <h2>Approved Alternative Foundation Systems</h2>
        <p className="intro-p">
          Our Installation Manuals provide one method to install your home. We
          recognize that there are numerous alternative stabilizing systems
          available to our builders, all of which could not be included in our
          installation manuals. The systems listed in this section have been
          designed by registered professional engineers and approved by third
          party inspection agencies (DAPIA) for use with our HUD coded homes.
          Systems listed in this section have met the requirements of the
          applicable HUD regulations, however their inclusion does not signify
          our endorsement of any particular system or product.
        </p>
        <p className="intro-p">
          Builders, please note that these systems are not approved for use with
          our modular coded homes.
        </p>

        <div className="foundation-systems-blurb">
          <div className="h5">Minute Man, Inc.</div>
          <a
            href="https://p.widencdn.net/pklavs/Minute_Man_Anchors"
            className="gtm-minute-men-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            Minute Man Anchors, Inc. Installation
          </a>
          <p className="caption">
            Instructions for Model LLBS Longitudinal and Lateral Bracing System
            Approved for Florida.
          </p>
        </div>

        <div className="foundation-systems-blurb">
          <div className="h5">Oliver Technologies, Inc.</div>
          <a
            href="https://p.widencdn.net/mz4goa/Oliver_Technologies_Inc"
            className="gtm-oliver-tech-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            Oliver Technologies, Inc. Installation
          </a>
          <p className="caption">
            Instructions for the 1100 &quot;V&quot; Series All Steel Foundation
            System.
          </p>
          <br />
          <a
            href="https://p.widencdn.net/n3xlub/Addendum---OT1102"
            className="gtm-oliver-tech-addendum"
            target="_blank"
            rel="noopener noreferrer"
          >
            Addendum
          </a>
          <p className="caption">Oliver Technologies 1102 Addendum</p>
        </div>

        <div className="foundation-systems-blurb">
          <div className="h5">Sure Safe</div>
          <a
            href="https://p.widencdn.net/yggsma/SURE-SAFE-EFS"
            className="gtm-sure-safe-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            Sure Safe EFS system
          </a>
          <p className="caption">
            Instructional documentation for using the Sure Safe&reg; EFS system.
            The Sure Safe&reg; EFS system has a HUD Permanent Foundation Guide
            compliant solution for manufactured homes that can be installed with
            the home in place.
          </p>
        </div>

        <div className="foundation-systems-blurb">
          <div className="h5">Tie Down Engineering</div>

          <a
            href="https://p.widencdn.net/23mluo/Vector_Dynamics_California"
            className="gtm-vector-dynamics-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            Vector Dynamics for California
          </a>
          <p className="caption">
            By Tie Down Engineering, Inc. Installation Instructions For The
            State Of California, Wind Zone 1, 15 PSF Wind, Seismic Zone 4.
          </p>
          <br />

          <a
            href="https://p.widencdn.net/him5mg/Vector_Dynamics-123"
            className="gtm-vector-dynamics-wind"
            target="_blank"
            rel="noopener noreferrer"
          >
            Vector Dynamics for Wind Zone 1, 2, and 3
          </a>
          <p className="caption">
            Wind Zone 1, 2, and 3 Installation Design Instructions.
          </p>
          <br />

          <a
            href="https://p.widencdn.net/qszke2/Xi2_California"
            className="gtm-xi2-california"
            target="_blank"
            rel="noopener noreferrer"
          >
            Xi2 for California
          </a>
          <p className="caption">
            Instructions for California for Ground and Concrete Systems
            California Building Code (CBC) 2007 Wind 85 mph, Exposure C; Seismic
            Design Category D By Tie Down Engineering.
          </p>
          <br />

          <a
            href="https://p.widencdn.net/uoou64/Xi2_123"
            className="gtm-xi2-wind"
            target="_blank"
            rel="noopener noreferrer"
          >
            Xi2 for Wind Zone 1, 2, and 3
          </a>
          <p className="caption">
            Instructions for Wind zone I, II and III By Tie Down Engineering.
          </p>
          <br />
        </div>
      </div>
    </BuilderResourcesStyles>
  );
};

export default BuilderResources;
